import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { F } from '../../styles/theme';

export default function PromoCard({ targetPlan = 'premium', onClick }) {
  const { isDark } = useTheme();
  const { t } = useT();

  const isFree = targetPlan === 'premium'; // premium = upgrade from free
  const label = isFree ? t('premium') : t('vip');
  const tagline = isFree
    ? t('premium_promo_features')
    : t('wid_vip_tagline');
  const cta = isFree ? `${t('upgrade')} · ${t('wid_promo_price')}` : t('redeem_code');

  const fg = isFree ? '#fff' : '#15151A';
  const fgMuted = isFree ? 'rgba(255,255,255,0.55)' : 'rgba(21,21,26,0.55)';
  const surfaceGrad = isFree
    ? 'linear-gradient(155deg, #FF6E60 0%, #E4544A 45%, #B83A3A 100%)'
    : 'linear-gradient(155deg, #EDEEF5 0%, #C8C9D2 50%, #9B9CA6 100%)';
  const glowColor = isFree ? 'rgba(255,160,150,0.40)' : 'rgba(255,255,255,0.55)';
  const dotColor = isFree ? '#fff' : '#15151A';
  const dotGlow = isFree ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)';
  const ctaBg = isFree
    ? 'linear-gradient(180deg, #FF8675 0%, #E4544A 100%)'
    : 'linear-gradient(180deg, #2C2D34 0%, #15151A 100%)';
  const ctaShadow = isFree
    ? '0 4px 16px rgba(120,20,20,0.45), inset 0 1px 0 rgba(255,255,255,0.30)'
    : '0 4px 16px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.16)';

  return (
    <div
      onClick={onClick}
      className="mo-row"
      style={{
        margin: '0 18px',
        height: 188,
        borderRadius: 22,
        background: surfaceGrad,
        boxShadow: `0 16px 40px rgba(0,0,0,${isDark ? 0.50 : 0.18}), inset 0 1px 0 rgba(255,255,255,${isFree ? 0.22 : 0.45})`,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        color: fg,
      }}
    >
      {/* corner radial glow */}
      <div style={{
        position: 'absolute', top: -50, right: -40, width: 240, height: 240,
        background: `radial-gradient(circle, ${glowColor} 0%, transparent 60%)`,
        pointerEvents: 'none',
      }} />

      {/* Giant centered "red." watermark */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <svg width="100%" height="100%" viewBox="0 0 320 188"
          preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
          <text x="160" y="138" textAnchor="middle"
            fontFamily="Plus Jakarta Sans, Manrope, system-ui" fontSize="180" fontWeight="800"
            letterSpacing="-10" fill={fg} opacity={isFree ? 0.20 : 0.16}>red.</text>
        </svg>
      </div>

      {/* Top row: plan label + dot + chevron */}
      <div style={{
        position: 'relative', zIndex: 2, padding: '18px 18px 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: F.display, fontSize: 17, fontWeight: 600,
            color: fg, letterSpacing: '-0.02em',
          }}>{label}</span>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: dotColor,
            boxShadow: `0 0 8px ${dotGlow}`,
          }} />
        </div>
      </div>

      {/* Bottom row: tagline + CTA */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
        padding: '12px 18px 18px',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          <div style={{
            fontFamily: F.family, fontSize: 10.5, fontWeight: 700,
            color: fgMuted, letterSpacing: '0.08em', textTransform: 'uppercase',
            marginBottom: 6,
          }}>{isFree ? t('wid_plan') : t('wid_activate')}</div>
          <div style={{
            fontFamily: F.family, fontSize: 14, fontWeight: 600,
            color: fg, letterSpacing: '-0.01em', lineHeight: 1.2, maxWidth: 180,
          }}>{tagline}</div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 14px', borderRadius: 99,
          background: ctaBg, color: '#fff', border: 'none',
          fontFamily: F.family, fontSize: 12, fontWeight: 700,
          letterSpacing: '-0.01em', cursor: 'pointer', whiteSpace: 'nowrap',
          boxShadow: ctaShadow,
        }}>
          {cta}
        </div>
      </div>
    </div>
  );
}
